import { all, takeEvery, put, call } from 'redux-saga/effects';
import { AI_TRANSLATE_JSON } from '../constants/AiTranslation';
import apiService from '../../services/ApiService';
import { extractJsonObject } from '../../utils/custom/extractJsonObject';
import { message } from 'antd';

const flatten = (obj, prefix = '', res = {}) => {
  for (let key in obj) {
    let propName = prefix ? prefix + '.' + key : key;
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      flatten(obj[key], propName, res);
    } else {
      res[propName] = obj[key];
    }
  }
  return res;
};

const unflatten = (data) => {
  const result = {};
  for (let i in data) {
    let keys = i.split('.');
    keys.reduce((acc, key, idx) => {
      if (acc[key] === undefined) {
        acc[key] = keys.length - 1 === idx ? data[i] : {};
      }
      return acc[key];
    }, result);
  }
  return result;
};

function* aiTranslateJsonWorker(action) {
  const { json, targetLang, sourceLang } = action.payload;

  if (!json || typeof json !== 'object') {
    yield put({
      type: AI_TRANSLATE_JSON.FULFILLED,
      payload: { resultJson: json, restoredKeys: null },
    });
    return;
  }

  try {
    const flatJson = flatten(json);
    const keys = Object.keys(flatJson);
    const chunkSize = 40;
    let translatedFlat = {};

    for (let i = 0; i < keys.length; i += chunkSize) {
      const chunkKeys = keys.slice(i, i + chunkSize);
      const chunkToTranslate = {};
      chunkKeys.forEach((k) => {
        chunkToTranslate[k] = flatJson[k];
      });

      const prompt = `
Task: Professional JSON Translation.
Context: Web application i18n strings.
Source Language: ${sourceLang}
Target Language: ${targetLang}

Rules:
1. Translate ONLY values. 
2. Keep keys identical to input (e.g., "settings.header.title").
3. DO NOT translate template variables like {{name}}, {{count}}, etc.
4. Output MUST be valid JSON only.

Input:
${JSON.stringify(chunkToTranslate, null, 2)}
`;

      try {
        const res = yield call([apiService, apiService.getAIResponse], { prompt });
        if (!res?.data?.success) continue;

        const rawText = res.data.response || '{}';
        let translatedChunk = null;

        try {
          translatedChunk = JSON.parse(rawText);
        } catch {
          try {
            translatedChunk = extractJsonObject(rawText);
          } catch (e) {
            console.error('Parse Error:', i, e);
          }
        }

        if (translatedChunk && typeof translatedChunk === 'object') {
          Object.assign(translatedFlat, translatedChunk);
        }
      } catch (err) {
        console.error('Request failed for chunk:', i, err);
      }
    }

    const restoredKeys = [];
    const finalFlat = {};

    Object.keys(flatJson).forEach((k) => {
      if (translatedFlat.hasOwnProperty(k)) {
        finalFlat[k] = translatedFlat[k];
      } else {
        finalFlat[k] = flatJson[k];
        restoredKeys.push({ key: k, value: flatJson[k] });
      }
    });

    const finalResult = unflatten(finalFlat);

    if (restoredKeys.length > 0) {
      yield call([message, message.info], 'Некоторые ключи были восстановлены из оригинала');
    }

    yield put({
      type: AI_TRANSLATE_JSON.FULFILLED,
      payload: {
        resultJson: finalResult,
        restoredKeys: restoredKeys.length ? restoredKeys : null,
      },
    });

    yield call([message, message.success], `AI translation ready for ${targetLang}`);
  } catch (error) {
    yield put({
      type: AI_TRANSLATE_JSON.REJECTED,
      payload: error.message,
    });
    yield call([message, message.error], 'AI translation failed');
  }
}

export function* watchAiTranslateJson() {
  yield takeEvery(AI_TRANSLATE_JSON.PENDING, aiTranslateJsonWorker);
}

export default function* rootSaga() {
  yield all([watchAiTranslateJson()]);
}
